<template>
  <div>
    <SectionTitle>
      <div class="flex justify-between items-center">
        <div>Signal Strength</div>
        <BaseClearButton
          @click="dateRangePopover?.$el?.present($event)"
          class="m-0 p-0 h-4"
          color="primary"
          size="small"
          >{{ selectedRange }}<i class="icon-chevron-down ml-1 text-lg"></i
        ></BaseClearButton>
        <IonPopover ref="dateRangePopover">
          <div class="p-2">
            <IonRadioGroup v-model="selectedRange">
              <IonRadio
                v-for="o of StartOptions"
                :key="o"
                :value="o"
                color="primary"
                @click="dateRangePopover?.$el?.dismiss()"
                justify="space-between"
                class="border-b border-gray-300 p-2 last-of-type:border-none"
                >{{ o }}</IonRadio
              >
            </IonRadioGroup>
          </div>
        </IonPopover>
      </div>
    </SectionTitle>
    <div class="m-2 aspect-video bg-gray-100 rounded-md flex items-center justify-center relative">
      <Transition name="fade">
        <svg v-show="!isLoading" ref="svgRef"></svg>
      </Transition>
      <Transition name="fade" mode="out-in">
        <div v-if="isLoading" class="chart-message">
          <IonSpinner color="primary" size="large" name="crescent"></IonSpinner>
        </div>
        <div v-else-if="noData" class="chart-message text-center">No Data Found<br />In Specified Range</div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { ChartService } from '@/http-services/chart.service';
import { SignalStrengthChartPoint, NetworkChartsDateRange } from 'common';
import { ref, useTemplateRef, watch } from 'vue';
import * as colors from '@/theme/colors.json';
import { IonPopover, IonRadio, IonRadioGroup, IonSpinner } from '@ionic/vue';
import { useToast } from '@/composables/use-toast';
import {
  Area,
  area,
  axisBottom,
  BaseType,
  extent,
  line,
  scaleLinear,
  scaleTime,
  select,
  Selection,
  timeFormat,
} from 'd3';

const props = defineProps<{
  propertyId?: string;
  repeaterId?: number;
}>();
const { errorToast } = useToast();
const dateRangePopover = useTemplateRef('dateRangePopover');
const StartOptions: NetworkChartsDateRange[] = Object.values(NetworkChartsDateRange);
const charts = new ChartService();
const selectedRange = ref<NetworkChartsDateRange>(StartOptions[1]);
const isLoading = ref(true);
const noData = ref(true);
const svgRef = ref();
const width = 500;
const height = 300;
const marginTop = 10;
const marginBottom = 55;
const marginLeft = 35;
const marginRight = 35;

watch(
  [() => props.propertyId, () => props.repeaterId, selectedRange],
  () => {
    fetchChartData();
  },
  { immediate: true }
);

async function fetchChartData() {
  if (props.propertyId && props.repeaterId) {
    isLoading.value = true;
    const { success, error, data } = await charts.getSignalStrengthHistory(
      props.propertyId,
      props.repeaterId,
      selectedRange.value
    );
    if (success) {
      noData.value = !data || !data.length;
      drawChart(data);
    } else {
      noData.value = true;
      errorToast(error);
    }
    isLoading.value = false;
  }
}

function initSvgDefs(
  svg: Selection<any, any, any, any>,
  data: SignalStrengthChartPoint[],
  a: Area<SignalStrengthChartPoint>
) {
  const defs = svg.append('defs');
  const maskGradient = defs
    .append('linearGradient')
    .attr('id', 'maskGradient')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '0%')
    .attr('y2', '100%');
  maskGradient.append('stop').attr('offset', '0%').style('stop-color', 'white');
  maskGradient.append('stop').attr('offset', '100%').style('stop-color', 'black');
  const areaHorizontalGrad = defs
    .append('linearGradient')
    .attr('id', 'areaHorizontalGrad')
    .attr('x1', '0%')
    .attr('x2', '100%')
    .attr('y1', '0%')
    .attr('y2', '0%');
  const lineHorizontalGrad = defs
    .append('linearGradient')
    .attr('id', 'lineHorizontalGrad')
    .attr('x1', '0%')
    .attr('x2', '100%')
    .attr('y1', '0%')
    .attr('y2', '0%');
  const stops: Array<{ color: string; offset: number; index?: number }> = [];
  const offsetScale = scaleTime(extent(data, (d) => d.timestamp) as [Date, Date], [0, 100]);
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    const color = d.signalStrengthPercentage >= 40 ? 'green' : 'yellow';
    const offset = offsetScale(d.timestamp);
    stops.push({ color, offset, index: i });
  }
  for (const stop of stops) {
    areaHorizontalGrad
      .append('stop')
      .attr('offset', `${stop.offset}%`)
      .attr('stop-color', stop.color === 'yellow' ? colors.yellow[300] : colors.green[300]);
    lineHorizontalGrad
      .append('stop')
      .attr('offset', `${stop.offset}%`)
      .attr('stop-color', stop.color === 'yellow' ? colors.yellow[500] : colors.green[500]);
  }
  const areaMask = defs.append('mask').attr('id', 'area-mask');
  areaMask.append('path').datum(data).attr('stroke', 'none').attr('d', a).attr('fill', 'url(#maskGradient)');
}

function drawChart(data: SignalStrengthChartPoint[]) {
  const xScale = scaleTime()
    .domain(extent(data, (d) => d.timestamp) as any)
    .range([marginLeft, width - marginRight]);
  const yScale = scaleLinear([0, 100], [height - marginBottom, marginTop]);
  const svg = select(svgRef.value)
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [0, 0, width, height])
    .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');
  svg.selectChildren().remove();
  if (!data.length) {
    return;
  }
  const l = line<SignalStrengthChartPoint>()
    .x((d) => xScale(d.timestamp))
    .y((d) => yScale(d.signalStrengthPercentage));
  const a = area<SignalStrengthChartPoint>()
    .x((d) => xScale(d.timestamp))
    .y0(yScale(0))
    .y1((d) => yScale(d.signalStrengthPercentage));
  initSvgDefs(svg, data, a);
  const timeFormatSpec =
    selectedRange.value === NetworkChartsDateRange.TWENTY_FOUR_HOURS ||
    selectedRange.value === NetworkChartsDateRange.PAST_3_DAYS
      ? '%a %_I %p'
      : '%b %e';
  const ticks = calculateTicks(xScale.domain(), 4);
  const xAxis = axisBottom(xScale)
    .tickFormat(timeFormat(timeFormatSpec) as any)
    .tickSize(0)
    .tickValues(ticks);
  // draw grid lines
  for (const gridPercent of [0, 25, 50, 75]) {
    svg
      .append('line')
      .attr('class', 'gridline')
      .attr('x1', marginLeft)
      .attr('x2', width - marginRight)
      .attr('y1', yScale(gridPercent))
      .attr('y2', yScale(gridPercent))
      .attr('stroke', colors.gray[300])
      .attr('stroke-width', 3);
  }

  // draw gradient area
  svg
    .append('path')
    .datum(data)
    .attr('stroke', 'none')
    .attr('d', a)
    .attr('fill', 'url(#areaHorizontalGrad)')
    .attr('mask', 'url(#area-mask)');
  // draw line
  svg
    .append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', 'url(#lineHorizontalGrad)')
    .attr('stroke-width', 3)
    .attr('d', l);

  // draw start and end dots on line
  for (const d of [data[0], data[data.length - 1]]) {
    svg
      .append('circle')
      .attr('class', 'markers')
      .attr('r', 4)
      .attr('cx', xScale(d.timestamp))
      .attr('cy', yScale(d.signalStrengthPercentage))
      .attr('fill', '#fff')
      .attr('stroke', d.signalStrengthPercentage >= 40 ? colors.green[500] : colors.yellow[500])
      .attr('stroke-width', 3);
  }

  // draw x axis (but hide default x axis line because our grid line looks better)
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height - marginBottom + 8})`)
    .call(xAxis)
    .selectAll('line')
    .attr('stroke', colors.gray[500])
    .attr('stroke-width', 1.5);
  svg.selectAll('.x-axis path').attr('stroke', 'none');
  svg
    .selectAll('.x-axis text')
    .attr('color', colors.gray['500'])
    .style('font-weight', 'bold')
    .style('font-size', '14px')
    .call(wrap);
}

function calculateTicks(domain: Date[], numTicks: number) {
  const start = domain[0];
  const end = domain[1];
  const tickValues = [];

  // Calculate the interval in milliseconds
  const interval = (end.getTime() - start.getTime()) / (numTicks - 1);

  // Generate tick values
  for (let i = 0; i < numTicks; i++) {
    const tickValue = new Date(start.getTime() + i * interval);
    tickValues.push(tickValue);
  }

  return tickValues;
}

function wrap(text: Selection<BaseType, any, any, any>) {
  text.each(function () {
    const text = select(this);
    const words = text.text().trim().split(/\s+/g);
    if (words.length >= 2) {
      const lineHeight = 1.1; // ems
      const y = text.attr('y');
      const dy = parseFloat(text.attr('dy')) || 0;
      text.text(null);
      text.append('tspan').attr('x', 0).attr('y', y).style('font-size', '12px').text(words[0]);
      text
        .append('tspan')
        .attr('x', 0)
        .attr('y', y)
        .attr('dy', lineHeight + dy + 'em')
        .text(words.slice(1).join(' '));
    }
  });
}
</script>
<style scoped>
.chart-message {
  @apply absolute inset-0 flex items-center justify-center font-medium text-gray-500 text-xl bg-transparent;
}
ion-spinner {
  width: 50px;
  height: 50px;
}
</style>
